/** @jsx h */
import { Fragment, h } from 'preact'
import Input from './input'
import Select from './select'
import SelectDownShift from './select-downshift'
import Button from './button'
import InputCheckboxRadio from './input-checkbox-radio'
import Step from './step'
import { useGlobal } from '../use-global'
import { useLabel } from '../use-label'
import { useForm, Controller } from 'react-hook-form'
import clsx from 'clsx'
import dayjs from 'dayjs'
import useAnalytics from '../use-analytics'
import { useEffect, useState } from 'preact/hooks'
import { Dialog } from './dialog'

const koreanConsents = [
  {
    name: 'PI_Consent_CollectPersonalInformation',
    required: true,
    options: [
      {
        label: 'PI_Consent_Agree',
        value: true,
        type: 'radio',
        required: true
      },
      {
        label: 'PI_Consent_Disagree',
        value: false,
        type: 'radio'
      }
    ],
    seeMore: true,
    seeMoreLabels: {
      header: 'PI_SeeMore1_Header',
      col1Title: 'PI_SeeMore1_Table_col1Title',
      col1Content: 'PI_SeeMore1_Table_col1Content',
      col2Title: 'PI_SeeMore1_Table_col2Title',
      col2Content: 'PI_SeeMore1_Table_col2Content',
      col3Title: 'PI_SeeMore1_Table_col3Title',
      col3Content: 'PI_SeeMore1_Table_col3Content',
      footer: 'PI_SeeMore1_Footer'
    }
  },
  {
    name: 'PI_Consent_SendMarketingCom',
    required: false,
    options: [
      {
        label: 'PI_Consent_Agree',
        value: true,
        type: 'radio'
      },
      {
        label: 'PI_Consent_Disagree',
        value: false,
        type: 'radio'
      }
    ],
    seeMore: true,
    seeMoreLabels: {
      header: 'PI_SeeMore2_Header',
      col1Title: 'PI_SeeMore2_Table_col1Title',
      col1Content: 'PI_SeeMore2_Table_col1Content',
      col2Title: 'PI_SeeMore2_Table_col2Title',
      col2Content: 'PI_SeeMore2_Table_col2Content',
      col3Title: 'PI_SeeMore2_Table_col3Title',
      col3Content: 'PI_SeeMore2_Table_col3Content',
      footer: 'PI_SeeMore2_Footer'
    }
  },
  {
    name: 'PI_Consent_ConfirmAge',
    required: true,
    seeMore: false,
    options: [
      {
        label: 'PI_Consent_ConfirmAge_description',
        value: true,
        type: 'checkbox'
      }
    ]
  }
]

const CardStepInformation = ({
  isOpen,
  isDone,
  onToggle,
  isEnabled,
  isHighlighted,
  conf,
  countries,
  phoneValidation = [],
  selectorClassName
}) => {
  const [seeMore1, setSeeMore1] = useState(false)
  const [seeMore2, setSeeMore2] = useState(false)
  const [phoneLength, setPhoneLength] = useState()
  const deduplicatedPhoneValidation = Object.values(
    phoneValidation.reduce((acc, val) => {
      acc[val.RICSC_Country__c] = val
      return acc
    }, {})
  )
  const t = useLabel()
  const analytics = useAnalytics()
  const [{ personalInfo, canvasConfig, selectedCountry }, dispatch] = useGlobal()

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    getValues,
    watch
  } = useForm({
    defaultValues: personalInfo
  })

  useEffect(() => {
    if (!getValues('PI_Mobile_Prefix')) return

    const phoneLengthArray = phoneValidation
      .filter(el => el.RICSC_Prefix__c === getValues('PI_Mobile_Prefix').value)
      .map(el => el.RICSC_Length__c)
      .sort((a, b) => a - b)

    const phoneLengthUnique = [...new Set(phoneLengthArray)].join(', ')

    setPhoneLength(phoneLengthUnique)
  }, [watch('PI_Mobile_Prefix')])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const subscription = watch(data => {
      dispatch({ type: 'save-personal-info-data-only', payload: data })
    })

    return () => subscription.unsubscribe()
  }, [isOpen, watch])

  let selectedCountryObj = {}
  const countryObjFromSF = countries.find(el => el.iso2Code === selectedCountry)?.originalName?.toUpperCase()

  if (countryObjFromSF) {
    const selectedCountryPhoneValidation = deduplicatedPhoneValidation.find(
      el => el.RICSC_Country__c.toUpperCase() === countryObjFromSF
    )
    if (selectedCountryPhoneValidation) {
      selectedCountryObj = selectedCountryPhoneValidation
    }
  }

  const prefixesList = selectedCountryObj.RICSC_Country__c
    ? [
        ...[deduplicatedPhoneValidation.find(el => el.RICSC_Country__c === selectedCountryObj.RICSC_Country__c)],
        ...deduplicatedPhoneValidation.filter(el => el.RICSC_Country__c !== selectedCountryObj.RICSC_Country__c)
      ].filter(Boolean)
    : phoneValidation

  return (
    <Step
      isOpen={isOpen}
      isDone={isDone}
      isEnabled={isEnabled}
      isHighlighted={isHighlighted}
      index={3}
      onToggle={() => {
        isEnabled && onToggle(3)
      }}
      className={selectorClassName}
      title={t('Personal_Information.PI_Personal_Information')}
      summary={
        selectedCountry === 'JP'
          ? (personalInfo.PI_LastName || '') +
            ' ' +
            (personalInfo.PI_FirstName || '') +
            ' - ' +
            (personalInfo.PI_Email_Address || '')
          : (personalInfo.PI_FirstName || '') +
            ' ' +
            (personalInfo.PI_LastName || '') +
            ' - ' +
            (personalInfo.PI_Email_Address || '')
      }
    >
      <form
        className='rcc_c_form'
        onSubmit={handleSubmit(data => {
          Object.keys(data).forEach(key => {
            if (typeof data[key] === 'string') {
              data[key] = data[key].trim()
            }
          })

          dispatch({ type: 'set-personal-info-data', payload: data })
          analytics.trackStep('baa_customer_information')
        })}
      >
        <div className='rcc_c_form-content rcc-flex rcc-flex-col rcc-gap-8'>
          <Controller
            name='PI_Title'
            control={control}
            rules={{ required: true }}
            defaultValue={personalInfo.PI_Title || ''}
            render={({ field: { value } }) => (
              <fieldset>
                <legend
                  className={clsx('rcc_c_content-title rcc-text-sm rcc-block rcc-mb-2.5', {
                    'rcc-text-danger': errors.PI_Title,
                    'rcc-text-gray': !errors.PI_Title,
                    'rcc-text-right': canvasConfig.direction === 'rtl'
                  })}
                >
                  {canvasConfig.locale === 'ar'
                    ? `* ${t('Personal_Information.PI_Title')}`
                    : `${t('Personal_Information.PI_Title')} *`}
                </legend>
                <div
                  className={clsx('rcc-flex rcc-gap-11', {
                    'rcc-text-danger': errors.PI_Title,
                    'rcc-text-text': !errors.PI_Title,
                    'rcc-flex-row-reverse': canvasConfig.direction === 'rtl'
                  })}
                >
                  <InputCheckboxRadio
                    hasError={errors.PI_Title}
                    checked={value === '0002'}
                    value='0002'
                    onChange={() => {
                      setValue('PI_Title', '0002')
                    }}
                    label={t('Personal_Information.PI_Title_Mr')}
                    type='radio'
                    id='rcc__Mr'
                  />
                  <InputCheckboxRadio
                    hasError={errors.PI_Title}
                    checked={value === '0006'}
                    value='0006'
                    onChange={() => {
                      setValue('PI_Title', '0006')
                    }}
                    label={t('Personal_Information.PI_Title_Ms')}
                    type='radio'
                    id='rcc__Ms'
                  />
                  {/* <InputCheckboxRadio
                    hasError={errors.PI_Title}
                    checked={value === '0144'}
                    value='0144'
                    onChange={() => {
                      setValue('PI_Title', '0144')
                    }}
                    label={t('Personal_Information.PI_Title_Mx')}
                    type='radio'
                    id='rcc__Mx'
                  /> */}
                </div>
                {errors.PI_Title && (
                  <p
                    className={clsx('rcc-mt-2 rcc-text-xs rcc-text-danger rcc-font-light', {
                      'rcc-text-right': canvasConfig.direction === 'rtl'
                    })}
                  >
                    {t('Personal_Information.ERR_PI_Title')}
                  </p>
                )}
              </fieldset>
            )}
          />

          {selectedCountry === 'JP' || selectedCountry === 'KR' ? (
            <Fragment>
              {selectedCountry === 'JP' && (
                <Controller
                  name='PI_FullName'
                  control={control}
                  rules={{
                    validate: {
                      unallowed: val => !/[!@#$%^&*()-/|{}]/.test(val)
                    },
                    required: true
                  }}
                  defaultValue={personalInfo.PI_FullName || ''}
                  render={({ field: { value } }) => (
                    <Input
                      id='pi_full_name'
                      hasError={errors.PI_FullName}
                      errorMessage={
                        errors.PI_FullName?.type === 'unallowed'
                          ? t('Personal_Information.ERR_PI_FullName_Unallowed', {
                              unallowed: '!@#$%^&*()-/|{}'
                            })
                          : t('Personal_Information.ERR_PI_FullName')
                      }
                      label={
                        canvasConfig.locale === 'ar'
                          ? `* ${t('Personal_Information.PI_FullName')}`
                          : `${t('Personal_Information.PI_FullName')} *`
                      }
                      value={value}
                      onChange={e => setValue('PI_FullName', e.target.value)}
                    />
                  )}
                />
              )}
              <Controller
                name='PI_LastName'
                control={control}
                rules={{
                  validate: {
                    unallowed: val => !/[!@#$%^&*()-/|{}]/.test(val)
                  },
                  required: true
                }}
                defaultValue={personalInfo.PI_LastName || ''}
                render={({ field: { value } }) => (
                  <Input
                    id='pi_last_name'
                    hasError={errors.PI_LastName}
                    errorMessage={
                      errors.PI_LastName?.type === 'unallowed'
                        ? t('Personal_Information.ERR_PI_LastName_Unallowed', {
                            unallowed: '!@#$%^&*()-/|{}'
                          })
                        : t('Personal_Information.ERR_PI_LastName')
                    }
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Personal_Information.PI_LastName')}`
                        : `${t('Personal_Information.PI_LastName')} *`
                    }
                    value={value}
                    maxLength={80}
                    onChange={e => setValue('PI_LastName', e.target.value)}
                  />
                )}
              />
              <Controller
                name='PI_FirstName'
                control={control}
                rules={{
                  validate: {
                    unallowed: val => !/[!@#$%^&*()-/|{}]/.test(val)
                  },
                  required: true
                }}
                defaultValue={personalInfo.PI_FirstName || ''}
                render={({ field: { value } }) => (
                  <Input
                    id='pi_first_name'
                    hasError={errors.PI_FirstName}
                    errorMessage={
                      errors.PI_FirstName?.type === 'unallowed'
                        ? t('Personal_Information.ERR_PI_FirstName_Unallowed', {
                            unallowed: '!@#$%^&*()-/|{}'
                          })
                        : t('Personal_Information.ERR_PI_FirstName')
                    }
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Personal_Information.PI_FirstName')}`
                        : `${t('Personal_Information.PI_FirstName')} *`
                    }
                    value={value}
                    maxLength={40}
                    onChange={e => setValue('PI_FirstName', e.target.value)}
                  />
                )}
              />
              {selectedCountry === 'KR' && (
                <>
                  <Controller
                    name='PI_OtherLastName'
                    control={control}
                    rules={{
                      pattern: /^[a-zA-Z ]*$/,
                      required: true
                    }}
                    defaultValue={personalInfo.PI_OtherLastName || ''}
                    render={({ field: { value } }) => (
                      <Input
                        id='pi_other_last_name'
                        hasError={errors.PI_OtherLastName}
                        errorMessage={
                          errors.PI_OtherLastName?.type === 'pattern'
                            ? t('Personal_Information.ERR_PI_OtherName_Unallowed')
                            : t('Personal_Information.ERR_PI_LastName')
                        }
                        label={
                          canvasConfig.locale === 'ar'
                            ? `* ${t('Personal_Information.PI_OtherLastName')}`
                            : `${t('Personal_Information.PI_OtherLastName')} *`
                        }
                        value={value}
                        maxLength={40}
                        onChange={e => setValue('PI_OtherLastName', e.target.value)}
                      />
                    )}
                  />
                  <Controller
                    name='PI_OtherFirstName'
                    control={control}
                    rules={{
                      pattern: /^[a-zA-Z ]*$/,
                      required: true
                    }}
                    defaultValue={personalInfo.PI_OtherFirstName || ''}
                    render={({ field: { value } }) => (
                      <Input
                        id='pi_other_first_name'
                        hasError={errors.PI_OtherFirstName}
                        errorMessage={
                          errors.PI_OtherFirstName?.type === 'pattern'
                            ? t('Personal_Information.ERR_PI_OtherName_Unallowed')
                            : t('Personal_Information.ERR_PI_FirstName')
                        }
                        label={
                          canvasConfig.locale === 'ar'
                            ? `* ${t('Personal_Information.PI_OtherFirstName')}`
                            : `${t('Personal_Information.PI_OtherFirstName')} *`
                        }
                        value={value}
                        maxLength={40}
                        onChange={e => setValue('PI_OtherFirstName', e.target.value)}
                      />
                    )}
                  />
                </>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <Controller
                name='PI_FirstName'
                control={control}
                rules={{
                  validate: {
                    unallowed: val => !/[!@#$%^&*()-/|{}]/.test(val)
                  },
                  required: true
                }}
                defaultValue={personalInfo.PI_FirstName || ''}
                render={({ field: { value } }) => (
                  <Input
                    id='pi_first_name'
                    hasError={errors.PI_FirstName}
                    errorMessage={
                      errors.PI_FirstName?.type === 'unallowed'
                        ? t('Personal_Information.ERR_PI_FirstName_Unallowed', {
                            unallowed: '!@#$%^&*()-/|{}'
                          })
                        : t('Personal_Information.ERR_PI_FirstName')
                    }
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Personal_Information.PI_FirstName')}`
                        : `${t('Personal_Information.PI_FirstName')} *`
                    }
                    value={value}
                    maxLength={40}
                    onChange={e => setValue('PI_FirstName', e.target.value)}
                  />
                )}
              />
              <Controller
                name='PI_LastName'
                control={control}
                rules={{
                  validate: {
                    unallowed: val => !/[!@#$%^&*()-/|{}]/.test(val)
                  },
                  required: true
                }}
                defaultValue={personalInfo.PI_LastName || ''}
                render={({ field: { value } }) => (
                  <Input
                    id='pi_last_name'
                    hasError={errors.PI_LastName}
                    errorMessage={
                      errors.PI_LastName?.type === 'unallowed'
                        ? t('Personal_Information.ERR_PI_LastName_Unallowed', {
                            unallowed: '!@#$%^&*()-/|{}'
                          })
                        : t('Personal_Information.ERR_PI_LastName')
                    }
                    label={
                      canvasConfig.locale === 'ar'
                        ? `* ${t('Personal_Information.PI_LastName')}`
                        : `${t('Personal_Information.PI_LastName')} *`
                    }
                    value={value}
                    maxLength={80}
                    onChange={e => setValue('PI_LastName', e.target.value)}
                  />
                )}
              />
            </Fragment>
          )}
          <fieldset>
            <legend
              className={clsx('rcc-text-sm rcc-block rcc-mb-2.5', {
                'rcc-text-danger': errors.PI_Mobile_Number,
                'rcc-text-gray': !errors.PI_Mobile_Number,
                'rcc-text-right': canvasConfig.direction === 'rtl'
              })}
            >
              {canvasConfig.locale === 'ar'
                ? `* ${t('Personal_Information.PI_Mobile_Number')}`
                : `${t('Personal_Information.PI_Mobile_Number')} *`}
            </legend>

            <div className={clsx('rcc-flex rcc-gap-5')}>
              <Controller
                name='PI_Mobile_Prefix'
                control={control}
                rules={{ required: true }}
                defaultValue={
                  personalInfo.PI_Mobile_Prefix
                    ? personalInfo.PI_Mobile_Prefix
                    : {
                        value: selectedCountryObj.RICSC_Prefix__c,
                        country: selectedCountryObj.RICSC_Country__c,
                        label: `${selectedCountryObj.RICSC_Country__c} ${selectedCountryObj.RICSC_Prefix__c}`,
                        length: selectedCountryObj.RICSC_Length__c
                      }
                }
                render={({ field: { value } }) => {
                  return (
                    <SelectDownShift
                      className='rcc-w-[100px] rcc-px-2'
                      hasError={errors.PI_Mobile_Number}
                      options={prefixesList.map(el => ({
                        value: `${el.RICSC_Prefix__c}`,
                        label: `${el.RICSC_Country__c} ${el.RICSC_Prefix__c}`,
                        country: el.RICSC_Country__c,
                        length: el.RICSC_Length__c
                      }))}
                      value={value?.value}
                      onChange={({ selectedItem }) => setValue('PI_Mobile_Prefix', selectedItem)}
                    />
                  )
                }}
              />
              <Controller
                name='PI_Mobile_Number'
                control={control}
                rules={{
                  validate: val => {
                    const storedValue = getValues('PI_Mobile_Prefix').value
                    const availableLengthValues = phoneValidation
                      .filter(el => el.RICSC_Prefix__c === storedValue)
                      .map(el => el.RICSC_Length__c)
                    return availableLengthValues.some(el => el === val.length)
                  },
                  required: true,
                  pattern: /^\d+$/
                }}
                defaultValue={personalInfo.PI_Mobile_Number || ''}
                render={({ field: { value } }) => (
                  <Input
                    id='pi_mobile_number'
                    className='rcc-flex-1'
                    hasError={errors.PI_Mobile_Number}
                    value={value}
                    onChange={e => setValue('PI_Mobile_Number', e.target.value.trim())}
                    ariaLabel='Mobile number'
                    type='tel'
                  />
                )}
              />
            </div>
            {errors.PI_Mobile_Number && (
              <p
                className={clsx('rcc-mt-2 rcc-text-xs rcc-text-danger rcc-font-light', {
                  'rcc-text-right': canvasConfig.direction === 'rtl'
                })}
              >
                {t('Personal_Information.ERR_PI_Mobile_Number', {
                  value: phoneLength
                })}
              </p>
            )}
          </fieldset>

          {selectedCountry === 'ES' && (
            <Controller
              name='PI_Id_Number'
              control={control}
              defaultValue={personalInfo.customerNationalId || ''}
              render={({ field: { value } }) => (
                <Input
                  id='pi_id_number'
                  hasError={errors.PI_Id_Number}
                  label={t('Personal_Information.PI_NationalIdNumber')}
                  value={value}
                  onChange={e => setValue('PI_Id_Number', e.target.value)}
                />
              )}
            />
          )}

          {conf.configurations.displayBirthday && (
            <Controller
              name='PI_Birthdate'
              control={control}
              rules={{
                pattern: dayjs().localeData().longDateFormat('L').startsWith('YYYY')
                  ? /^(\d{4})\/(\d{2})\/(\d{2})$/
                  : /^(\d{2})\/(\d{2})\/(\d{4})$/
              }}
              defaultValue={personalInfo.PI_Birthdate || ''}
              render={({ field: { value } }) => (
                <Input
                  id='pi_birthdate'
                  dateMask
                  hasError={errors.PI_Birthdate}
                  errorMessage={t('Personal_Information.ERR_PI_Birthdate')}
                  label={`${t('Personal_Information.PI_Birthdate')} ${dayjs().localeData().longDateFormat('L')}`}
                  value={value}
                  onChange={date => setValue('PI_Birthdate', date)}
                />
              )}
            />
          )}
          <Controller
            name='PI_Email_Address'
            control={control}
            rules={{
              required: true,
              pattern:
                /^(?:[a-z0-9+!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])$/i
            }}
            defaultValue={personalInfo.PI_Email_Address || ''}
            render={({ field: { value } }) => (
              <Input
                id='pi_email_address'
                hasError={errors.PI_Email_Address}
                errorMessage={t('Personal_Information.ERR_PI_Email_Address')}
                label={
                  canvasConfig.locale === 'ar'
                    ? `* ${t('Personal_Information.PI_Email_Address')}`
                    : `${t('Personal_Information.PI_Email_Address')} *`
                }
                value={value}
                onChange={e => setValue('PI_Email_Address', e.target.value.trim())}
              />
            )}
          />
          <Controller
            name='PI_Country_Of_Residence'
            control={control}
            rules={{ required: true }}
            defaultValue={personalInfo.PI_Country_Of_Residence || selectedCountry}
            render={({ field: { value } }) => (
              <Select
                id='pi_place_of_residence'
                hasError={errors.PI_Country_Of_Residence}
                label={
                  canvasConfig.locale === 'ar'
                    ? `* ${t('Personal_Information.PI_Country_Of_Residence')}`
                    : `${t('Personal_Information.PI_Country_Of_Residence')} *`
                }
                options={[
                  countries.find(el => el.iso2Code === selectedCountry),
                  ...countries.filter(el => el.iso2Code !== selectedCountry)
                ]
                  .filter(el => el)
                  .map(el => {
                    return {
                      value: el?.iso2Code,
                      label: el?.name
                    }
                  })}
                value={value}
                onChange={e => {
                  // surrounded by try/catch because this piece of logic might be unsafe.
                  // we don't want to break the UI in case something goes wrong
                  // worst case scenario we simply do not update the prefix value
                  try {
                    const selectedCountry = countries.find(el => el.iso2Code === e.target.value)

                    const nextPhonePrefix = prefixesList.find(
                      el => el.RICSC_Country__c.toUpperCase() === selectedCountry.originalName.toUpperCase()
                    )
                    // We do not want to change prefix if phone number is already set
                    if (nextPhonePrefix && !getValues('PI_Mobile_Number')) {
                      setValue('PI_Mobile_Prefix', {
                        value: nextPhonePrefix.RICSC_Prefix__c,
                        country: nextPhonePrefix.RICSC_Country__c,
                        label: `${nextPhonePrefix.RICSC_Country__c} ${nextPhonePrefix.RICSC_Prefix__c}`,
                        length: nextPhonePrefix.RICSC_Length__c
                      })
                    }
                  } catch (err) {}
                  setValue('PI_Country_Of_Residence', e.target.value)
                }}
              />
            )}
          />
        </div>
        <div className='rcc-flex rcc-flex-col rcc-mt-8 rcc-gap-6'>
          {selectedCountry === 'KR' && (
            <>
              {koreanConsents.map(el => (
                <Controller
                  key={el.name}
                  name={el.name}
                  control={control}
                  rules={{
                    validate: value => {
                      if (value === '' || (el.required && value === false)) {
                        return false
                      }

                      return true
                    }
                  }}
                  defaultValue={personalInfo[el.name] || ''}
                  render={({ field: { value } }) => (
                    <fieldset>
                      <legend
                        className={clsx('rcc_c_content-title rcc-text-sm rcc-block rcc-mb-1 rcc-pl-0', {
                          'rcc-text-danger': errors[el.name],
                          'rcc-text-gray': !errors[el.name],
                          'rcc-text-right': canvasConfig.direction === 'rtl'
                        })}
                      >
                        {t(`Personal_Information.${el.name}`)}
                      </legend>
                      <div
                        className={clsx('rcc-flex rcc-gap-11', {
                          'rcc-text-danger': errors[el.name],
                          'rcc-text-text': !errors[el.name],
                          'rcc-flex-row-reverse': canvasConfig.direction === 'rtl'
                        })}
                      >
                        {el.options.map(option => (
                          <InputCheckboxRadio
                            key={option.label}
                            hasError={errors[el.name]}
                            checked={value === option.value}
                            value={option.value}
                            onChange={e => setValue(el.name, option.type === 'radio' ? option.value : e.target.checked)}
                            label={`${t(`Personal_Information.${option.label}`)}${option.required ? ' *' : ''}`}
                            type={option.type}
                            id={`rcc__${el.name}_${option.label}`}
                          />
                        ))}
                      </div>

                      {el.seeMore && (
                        <>
                          <button
                            className={`rcc-self-baseline rcc-bg-transparent rcc-cursor-pointer rcc-underline rcc-p-0 rcc-mt-3 rcc-text-gray`}
                            onClick={e => {
                              e.preventDefault()
                              if (el.name === 'PI_Consent_CollectPersonalInformation') {
                                setSeeMore1(true)
                              } else {
                                setSeeMore2(true)
                              }
                            }}
                          >
                            {t(`Personal_Information.PI_Consent_SeeMore`)}
                          </button>

                          <Dialog
                            isOpen={el.name === 'PI_Consent_CollectPersonalInformation' ? seeMore1 : seeMore2}
                            onClose={() => {
                              if (el.name === 'PI_Consent_CollectPersonalInformation') {
                                setSeeMore1(false)
                              } else {
                                setSeeMore2(false)
                              }
                            }}
                          >
                            <p>
                              {t(`Personal_Information.${el.seeMoreLabels.header}`, {
                                maison: canvasConfig.maisonName
                              })}
                            </p>

                            <table className='rcc-border-collapse rcc-bg-gray-very-light rcc-clear-both rcc-my-4'>
                              <thead className='mobile:rcc-hidden'>
                                <th className='rcc-border-b-4 rcc-border-white rcc-p-2'>
                                  {t(`Personal_Information.${el.seeMoreLabels.col1Title}`)}
                                </th>
                                <th className='rcc-border-b-4 rcc-border-white rcc-p-2'>
                                  {t(`Personal_Information.${el.seeMoreLabels.col2Title}`)}
                                </th>
                                <th className='rcc-border-b-4 rcc-border-white rcc-p-2'>
                                  {t(`Personal_Information.${el.seeMoreLabels.col3Title}`)}
                                </th>
                              </thead>
                              <tbody>
                                <tr className='mobile:rcc-flex mobile:rcc-flex-col'>
                                  <td className='rcc-p-4 rcc-align-top'>
                                    <p className='rcc-hidden rcc-font-bold mobile:rcc-block'>
                                      {t(`Personal_Information.${el.seeMoreLabels.col1Title}`)}
                                    </p>
                                    <span>{t(`Personal_Information.${el.seeMoreLabels.col1Content}`)}</span>
                                  </td>
                                  <td className='rcc-p-4 rcc-align-top'>
                                    <p className='rcc-hidden rcc-font-bold mobile:rcc-block'>
                                      {t(`Personal_Information.${el.seeMoreLabels.col2Title}`)}
                                    </p>
                                    <span
                                      className={clsx({
                                        'rcc-underline rcc-text-xl': el.name === 'PI_Consent_CollectPersonalInformation'
                                      })}
                                    >
                                      {t(`Personal_Information.${el.seeMoreLabels.col2Content}`)}
                                    </span>
                                  </td>
                                  <td className='rcc-p-4 rcc-align-top'>
                                    <p className='rcc-hidden rcc-font-bold mobile:rcc-block'>
                                      {t(`Personal_Information.${el.seeMoreLabels.col3Title}`)}
                                    </p>
                                    <span className='rcc-underline rcc-text-xl'>
                                      {t(`Personal_Information.${el.seeMoreLabels.col3Content}`)}
                                    </span>
                                  </td>
                                </tr>
                              </tbody>
                            </table>

                            <p>{t(`Personal_Information.${el.seeMoreLabels.footer}`)}</p>
                          </Dialog>
                        </>
                      )}
                    </fieldset>
                  )}
                />
              ))}
              <p
                dangerouslySetInnerHTML={{
                  __html: t(`Personal_Information.PI_Consent_PrivacyPolicy`, {
                    maison: canvasConfig.maisonName
                  })
                }}
              ></p>
            </>
          )}
          {selectedCountry !== 'KR' && conf.configurations.displayCustomerOptIn && (
            <Controller
              name='PI_Consent_SendMarketingCom'
              defaultValue={!!personalInfo.PI_Consent_SendMarketingCom}
              control={control}
              render={() => {
                return (
                  <>
                    {canvasConfig.locale === 'ar' && (
                      <label
                        className={clsx('rcc_c_custom-input rcc-block rcc-cursor-pointer rcc-relative rcc-leading-6', {
                          'rcc_rtl rcc-text-right': canvasConfig.direction === 'rtl'
                        })}
                        dangerouslySetInnerHTML={{
                          __html: t(`Personal_Information.PI_Pre_Opt_in_${canvasConfig.maisonId}`)
                        }}
                      />
                    )}

                    <InputCheckboxRadio
                      checked={!!getValues(`PI_Consent_SendMarketingCom`)}
                      onChange={e => setValue('PI_Consent_SendMarketingCom', e.target.checked)}
                      label={t(`Personal_Information.PI_Opt_in_${canvasConfig.maisonId}`)}
                      type='checkbox'
                      id='rcc_PI_Consent_SendMarketingCom'
                    />
                  </>
                )
              }}
            />
          )}
        </div>
        {conf.configurations.communicationPreferences && conf.configurations.displayCommunicationPreferences && (
          <div className='rcc_c_container-group-input'>
            <hr className='rcc_c_card-step-content-hr rcc-my-5 rcc-w-full rcc-bg-gray-very-light' />
            <p className='rcc_c_content-title rcc-text-text rcc-text-center rcc-mt-11'>
              {t('Personal_Information.PI_Communication_Preferences')}
            </p>
            <div className='rcc_c_group-input-communication rcc-flex rcc-flex-wrap'>
              {conf.configurations.communicationPreferences.split(';').map((el, i) => (
                <Controller
                  key={el}
                  name={`communicationPref[${i}]`}
                  defaultValue={false}
                  control={control}
                  render={() => {
                    return (
                      <InputCheckboxRadio
                        checked={!!getValues(`communicationPref[${i}]`)}
                        onChange={e => {
                          setValue(`communicationPref[${i}]`, e.target.checked ? el : false)
                        }}
                        label={el}
                        type='checkbox'
                        id={`rcc_${el}`}
                      />
                    )
                  }}
                />
              ))}
            </div>
          </div>
        )}
        {conf.configurations.displayPersonalDataLegacy ? (
          <div className='rcc_c_container-group-input'>
            <hr className='rcc_c_card-step-content-hr rcc-my-5 rcc-w-full rcc-bg-gray-very-light' />
            <p
              className={clsx('rcc_c_content-title rcc-text-center rcc-mt-11 rcc-mb-3.5', {
                'rcc-text-danger': errors.legacy,
                'rcc-text-text': !errors.legacy
              })}
            >
              {t('Personal_Information.PI_Personal_Data_Legacy_Lable')}
            </p>
            <Controller
              name='legacy'
              control={control}
              rules={{ required: true }}
              defaultValue={!!personalInfo.legacy}
              render={() => (
                <InputCheckboxRadio
                  hasError={errors.legacy}
                  checked={!!getValues('legacy')}
                  value={true}
                  onChange={e => {
                    setValue('legacy', e.target.checked)
                  }}
                  label={t('Personal_Information.PI_Personal_Data_Legacy_Context')}
                  id='rcc__accordance'
                />
              )}
            />
          </div>
        ) : null}

        <Button type='submit' className='rcc_c_form-btn-submit'>
          {t('Global.Confirm')}
        </Button>
      </form>
    </Step>
  )
}

export default CardStepInformation
